'use client';

import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import type React from 'react';
import type { ReactNode } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton } from 'react-aria-components';

import Icon from '../Icon/Icon';
import Loader from '../Loader/Loader';
import { useFusionTheme } from '../hooks';

import styles from './Button.module.scss';

export type BaseButtonProps = Omit<AriaButtonProps, 'className' | 'children'> &
  Omit<VariantProps<typeof buttonVariants>, 'iconPosition'> & {
    className?: string;
  };

export type ButtonChildrenProps =
  | {
      children?: ReactNode;
      icon?: React.FC<React.SVGProps<SVGElement>>;
      iconPosition?: 'left' | 'right';
    }
  | {
      children: (props: RenderProp) => React.ReactElement;
      icon?: never;
      iconPosition?: never;
    };

export type ButtonProps = BaseButtonProps & ButtonChildrenProps;

export type RenderProp = Omit<
  ButtonProps,
  'children' | 'isHovered' | 'isPressed' | 'isFocused' | 'isFocusVisible' | 'icon' | 'iconPosition'
> & {
  className: string;
};

const buttonVariants = cva(styles.button, {
  variants: {
    variant: {
      primary: styles.variantPrimary,
      secondary: styles.variantSecondary,
      tertiary: styles.variantTertiary
    },
    size: {
      small: styles.sizeSmall,
      default: styles.sizeDefault,
      large: styles.sizeLarge
    },
    width: {
      full: styles.full,
      hug: ''
    },
    noPadding: {
      true: styles.noPadding
    },
    appearance: {
      fill: ''
    }
  },
  compoundVariants: [
    {
      variant: 'secondary',
      size: 'small',
      appearance: 'fill',
      className: styles.appearanceFill
    }
  ]
});

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'default',
      noPadding,
      iconPosition,
      icon,
      className,
      width = 'hug',
      children,
      appearance,
      ...buttonProps
    },
    forwardedRef
  ) => {
    const { addThemeClassName } = useFusionTheme();

    const resolvedClassName = addThemeClassName(
      buttonVariants({
        variant,
        size,
        width,
        noPadding,
        appearance
      }),
      iconPosition === 'right' ? styles.reverse : '',
      className
    );

    // Check if children is a function (render prop pattern)
    if (!buttonProps.isPending && typeof children === 'function') {
      const renderProps: RenderProp = {
        ...buttonProps,
        variant,
        size,
        width,
        noPadding,
        appearance,
        className: resolvedClassName
      };

      return children(renderProps);
    }

    const renderChildContent = () => {
      if (buttonProps.isPending) return <Loader />;

      if ((typeof children !== 'function' && children) || children === 0) {
        return <span className={styles.textContainer}>{children}</span>;
      }
    };

    return (
      <AriaButton ref={forwardedRef} className={resolvedClassName} {...buttonProps}>
        {!buttonProps.isPending && icon && <Icon icon={icon} aria-hidden />}
        {renderChildContent()}
      </AriaButton>
    );
  }
);

export default Button;
